import React from "react";
import mergeLeft from "ramda/src/mergeLeft";
import { useHistory } from "react-router";
import compose from "ramda/src/compose";
import andThen from "ramda/src/andThen";
import styled from "styled-components";
import MUILink from "@mui/material/Link";

import { Grid } from "../../UI/Grid";
import { Typography } from "../../UI/Typography";
import { useNetwork } from "../../hooks/useNetwork";
import { useUserSearch } from "../../hooks/useUserSearch";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../constants/general";
import { URL } from "../../constants/navigation";
import { UserInviteSearchResultsTable } from "../UserInviteSearchResultsTable";
import { ByNameSearchComponent } from "../../routes/users/search/ByNameSearchComponent";

export const Link = styled(MUILink)`
  cursor: pointer;
  display: inline-block;
  color: #007bff !important;
`;

const initialState: Record<string, any> = {
  firstName: "",
  lastName: "",
  pin: undefined,
  userId: "",
  globalSearch: false,
};

export const UserInviteByName = () => {
  const { push } = useHistory();
  const [searchBy, setSearchBy] = React.useState(initialState);
  const { selectedNetwork } = useNetwork();
  const { results, submit, isLoading } = useUserSearch(
    selectedNetwork.l1Id,
    searchBy.firstName,
    searchBy.lastName,
    null,
    searchBy.globalSearch,
    searchBy.userId
  );
  const { isAcc, isEntrata } = useSelectedNetworkImportTypes();
  const setDefaultValues = () => {
    setSearchBy(initialState);
  };

  const handleIntegrationClick = () => push(URL.INTEGRATIONS);

  const BasicInfoText = () => (<>
    <Typography gutterBottom>
      You can resend the RoomSync invitation to any imported resident by searching for them below. If the resident
      does not appear in search, they have not been sent to RoomSync by your housing management system.
    </Typography>
    <Typography gutterBottom>
      You can refer to your <Link onClick={handleIntegrationClick}>integration settings</Link> to determine why a
      user may not have been imported. If these settings don't solve the issue, email {EMAIL_ADDRESS} or call{" "}
      {PHONE_NUMBER} for further assistance.
    </Typography>
    <Typography>
      <strong>
        If the resident has already registered for this matching cycle, they no longer need an invite link (the one
        they clicked is now claimed and will not work).
      </strong>{" "}
      Instead, share the resident's login method with them so they can login to their account when they return to
      the app on their mobile device or visit{" "}
      <Link target="_blank" href="https://web.roomsync.com">
        web.roomsync.com
      </Link>
      .
    </Typography>
  </>);

  const EntrataAccTypeInfoText = () => (<>
    <Typography gutterBottom>
    If a user has already been imported through your <Link onClick={handleIntegrationClick}>integration settings</Link>,
    you can search for them by name to resend their invitation. 
    If they have already registered using their link, you can view their login method to share with them.
    </Typography>
  </>);
  const entrataAccBottomList = (results: any[], networkType: string) => (
    <ul>
      {results.length > 0 && networkType === "entrata" &&
        <li>It has been at least 24 hours since the resident's lease has been <strong>approved</strong> in Entrata</li>}
      {results.length === 0 && networkType === "entrata" &&
        <li>It has been at least 24 hours since the resident's lease has been <strong>approved</strong> in Entrata</li>}
      {networkType === "entrata" &&    
      <li>The lease is compatible with your <Link onClick={handleIntegrationClick}>integration settings</Link>&nbsp;
          for Entrata Floor Plan, Space Option and Lease Start Date</li>
      }

      {networkType === "acc" &&
        <li>It has been at least 24 hours since the resident's lease has signed their lease in Salesforce</li>}
      {networkType === "acc" &&
        <li>The lease is compatible with your <Link onClick={handleIntegrationClick}>integration settings</Link>&nbsp;
          for Floor Plan and Lease Start Date</li>
      }
      {networkType === "acc" &&
        <li>The 'waitlisted' checkbox is not checked by mistake in their lease contract</li>
      }
    </ul>
  );

  const entrataAccBottomText = (results: any[], networkType: string) => (<>
    {results.length > 0 && 
      <Typography gutterBottom>
        If you do not see the user you are searching for, please ensure the following:
      </Typography>
    }
    {results.length === 0 && 
      <Typography gutterBottom>
        This user has not been imported yet. Please ensure the following:
      </Typography>
    }
    <Typography>
      {entrataAccBottomList(results, networkType)}
    </Typography>
    <Typography>
      Please email <Link href={`mailto:clients@roomsync.com`}>clients@roomsync.com</Link>&nbsp; or call{" "} {PHONE_NUMBER} if you still require assistance with importing a resident to RoomSync.
    </Typography>
  </>);
  

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isEntrata || isAcc?
          <EntrataAccTypeInfoText /> :
          <BasicInfoText />
        }
        
      </Grid>
      <Grid item xs={12}>
        <ByNameSearchComponent
          lastName={searchBy.lastName}
          firstName={searchBy.firstName}
          isLoading={isLoading}
          setFirstName={(event: any) => {
            setSearchBy(mergeLeft({ firstName: event.target.value }));
          }}
          setLastName={(event: any) => {
            setSearchBy(mergeLeft({ lastName: event.target.value }));
          }}
          setShouldSearch={compose(andThen(setDefaultValues), submit)}
          disableGlobalSearch
          setGlobalSearch={(event: any) => {
            setSearchBy(mergeLeft({ globalSearch: event.target.checked }));
          }}
        />
      </Grid>
      {results.results.length > 0 &&
        <Grid item xs={12} my={4}>
          <UserInviteSearchResultsTable
            results={results.results}
            searched={results.searched}
            isGlobalSearch={searchBy.globalSearch}
          />
        </Grid>
      }
      <Grid item xs={12}>
        {((isEntrata || isAcc) && results.searched) && entrataAccBottomText(results.results, isAcc? "acc": "entrata")}
      </Grid>
    </Grid>
  );
};
